import { ReactNode, useMemo } from 'react';
import { useQuery } from '@apollo/client';

import StoreUrlRequest from 'components/FormRequestBlock/StoreUrl';
import ProductUrlRequest from 'components/FormRequestBlock/ProductUrl';
import InventoryRequestsTable from 'components/InventoryRequestsTable';

import './style.scss';
import { useAppSelector } from 'app/store';
import { INVENTORY_REQUESTS_DATA } from 'app/graphql/admin-api';
import { selectUserInfo } from 'app/store/user';
import { Progress } from 'components/ui/progress';
import { Typography } from 'components/ui/typography';

const sectionHeaderCn = 'mb-5 mt-16 first-of-type:mt-0';

const DEFAULT_UNIQUE_STORE_REQUEST_LIMIT = 10;
const DEFAULT_UNIQUE_PRODUCT_REQUEST_LIMIT = 300;
const UNLIMITED_REQUESTS = -1;

const Page = () => {
  const user = useAppSelector(selectUserInfo);

  const { data, loading, refetch } = useQuery(INVENTORY_REQUESTS_DATA, {
    context: { clientName: 'admin' },
    fetchPolicy: 'cache-and-network',
  });

  const list = useMemo(() => {
    const items = data?.inventoryRequests.edges.map((edge) => edge.node) ?? [];
    return [...items].sort(
      (a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime(),
    );
  }, [data]);

  const storeRequestCount = data?.storeInventoryRequestsCount ?? 0;
  const productRequestCount = data?.productInventoryRequestsCount ?? 0;

  const storeLimit = useMemo(() => {
    if (user?.inventoryRequestLimits?.uniqueStoreRequests === UNLIMITED_REQUESTS) {
      return UNLIMITED_REQUESTS;
    }
    return user?.inventoryRequestLimits?.uniqueStoreRequests ?? DEFAULT_UNIQUE_STORE_REQUEST_LIMIT;
  }, [user]);

  const productLimit = useMemo(() => {
    if (user?.inventoryRequestLimits?.uniqueProductRequests === UNLIMITED_REQUESTS) {
      return UNLIMITED_REQUESTS;
    }
    return (
      user?.inventoryRequestLimits?.uniqueProductRequests ?? DEFAULT_UNIQUE_PRODUCT_REQUEST_LIMIT
    );
  }, [user]);

  const storeRequestCountPercentage = (storeRequestCount / storeLimit) * 100;
  const productRequestCountPercentage = (productRequestCount / productLimit) * 100;

  return (
    <div className="requests-container">
      <Typography variant="h3" as="h2" className={sectionHeaderCn}>
        Store Inventory
      </Typography>
      <StoreUrlRequest onRequestComplete={refetch} />

      <Typography variant="h3" as="h2" className={sectionHeaderCn}>
        Product Inventory
      </Typography>
      <ProductUrlRequest onRequestComplete={refetch} />

      <Typography variant="h3" as="h2" className={sectionHeaderCn}>
        Request History
      </Typography>
      <div className="mb-5 flex flex-col gap-2">
        <RequestRow>
          <span>Stores Requested:</span>
          <Progress
            className="h-2 w-52"
            value={
              storeLimit === UNLIMITED_REQUESTS
                ? 0
                : Math.max(0, Math.min(100, storeRequestCountPercentage))
            }
          />
          <span>
            {storeRequestCount}/{storeLimit === UNLIMITED_REQUESTS ? '∞' : storeLimit}
          </span>
        </RequestRow>
        <RequestRow>
          <span>Products Requested:</span>
          <Progress
            className="h-2 w-52"
            value={
              productLimit === UNLIMITED_REQUESTS
                ? 0
                : Math.max(0, Math.min(100, productRequestCountPercentage))
            }
          />
          <span>
            {productRequestCount}/{productLimit === UNLIMITED_REQUESTS ? '∞' : productLimit}
          </span>
        </RequestRow>
      </div>
      <InventoryRequestsTable list={list} loading={loading} />
    </div>
  );
};

const RequestRow = ({ children }: { children: ReactNode }) => (
  <Typography as="div" variant="body" className="flex flex-row items-center gap-2">
    {children}
  </Typography>
);

export default Page;
